import React, { useState, useEffect } from "react";
import { FaSyncAlt } from "react-icons/fa";
import axios from 'axios';

// Platform icons mapping
const platformIcons = {
  facebook: <FaFacebook className="text-blue-600" />,
  instagram: <FaInstagram className="text-pink-600" />,
  twitter: <FaTwitter className="text-blue-400" />,
  linkedin: <FaLinkedin className="text-blue-700" />,
  youtube: <FaYoutube className="text-red-600" />,
  tiktok: <FaTiktok className="text-black" />,
  pinterest: <FaPinterest className="text-red-500" />,
};

// Platform names mapping
const platformNames = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  pinterest: 'Pinterest'
};

// Import required icons
import {
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin,
  FaYoutube, FaTiktok, FaPinterest
} from 'react-icons/fa';
import SocialMediadashboardSidebar from "../SocialMediadashboardSidebar";

const ContentAnalytics = () => {
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [error, setError] = useState(null);

  // Format numbers function
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Fetch Facebook posts with insights
  const fetchFacebookPosts = async (pageAccessToken, pageId) => {
    try {
      console.log(`Fetching Facebook posts for page ${pageId}`);
      
      // First get posts with basic metrics
      const postsResponse = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}/posts?fields=id,message,created_time,likes.limit(1).summary(true),comments.limit(1).summary(true),shares,full_picture,permalink_url&limit=10&access_token=${pageAccessToken}`
      );
      
      console.log('Facebook posts response:', postsResponse.data);

      if (!postsResponse.data.data || postsResponse.data.data.length === 0) {
        return [];
      }

      // For each post, fetch insights to get real view data
      const postsWithInsights = await Promise.all(
        postsResponse.data.data.map(async (post) => {
          try {
            // Fetch post insights for impressions (views)
            const insightsResponse = await axios.get(
              `https://graph.facebook.com/v19.0/${post.id}/insights?metric=post_impressions,post_impressions_unique,post_engaged_users,post_video_avg_time_watched&access_token=${pageAccessToken}`
            );

            console.log(`Insights for post ${post.id}:`, insightsResponse.data);

            let views = 0;
            let watchTime = 0;

            if (insightsResponse.data.data) {
              insightsResponse.data.data.forEach(insight => {
                if (insight.name === 'post_impressions' && insight.values[0]) {
                  views = insight.values[0].value;
                }
                if (insight.name === 'post_video_avg_time_watched' && insight.values[0]) {
                  watchTime = Math.round(insight.values[0].value / 60); // Convert seconds to minutes
                }
              });
            }

            return {
              id: post.id,
              title: post.message?.substring(0, 60) + (post.message?.length > 60 ? '...' : '') || 'Photo/Video Post',
              description: post.message || 'No description available',
              date: new Date(post.created_time).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              }),
              views: views,
              comments: post.comments?.summary?.total_count || 0,
              likes: post.likes?.summary?.total_count || 0,
              shares: post.shares?.count || 0,
              watchTime: watchTime || 0,
              platform: 'facebook',
              image: post.full_picture,
              url: post.permalink_url,
              created_time: post.created_time,
              // Raw data for debugging
              _raw: post
            };
          } catch (insightError) {
            console.error(`Error fetching insights for post ${post.id}:`, insightError);
            // Return post without insights if insights fetch fails
            return {
              id: post.id,
              title: post.message?.substring(0, 60) + (post.message?.length > 60 ? '...' : '') || 'Photo/Video Post',
              description: post.message || 'No description available',
              date: new Date(post.created_time).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              }),
              views: 0,
              comments: post.comments?.summary?.total_count || 0,
              likes: post.likes?.summary?.total_count || 0,
              shares: post.shares?.count || 0,
              watchTime: 0,
              platform: 'facebook',
              image: post.full_picture,
              url: post.permalink_url,
              created_time: post.created_time,
              _raw: post
            };
          }
        })
      );

      return postsWithInsights;
    } catch (error) {
      console.error('Error fetching Facebook posts:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw new Error(`Failed to fetch Facebook posts: ${error.response?.data?.error?.message || error.message}`);
    }
  };

  // Fetch Instagram posts (if you have Instagram Graph API access)
  const fetchInstagramPosts = async (pageAccessToken, instagramAccountId) => {
    try {
      console.log(`Fetching Instagram posts for account ${instagramAccountId}`);
      
      const postsResponse = await axios.get(
        `https://graph.facebook.com/v19.0/${instagramAccountId}/media?fields=id,caption,media_type,media_url,permalink,timestamp,like_count,comments_count&limit=10&access_token=${pageAccessToken}`
      );

      console.log('Instagram posts response:', postsResponse.data);

      if (!postsResponse.data.data || postsResponse.data.data.length === 0) {
        return [];
      }

      // For each Instagram post, fetch insights
      const postsWithInsights = await Promise.all(
        postsResponse.data.data.map(async (post) => {
          try {
            // Fetch Instagram media insights
            const insightsResponse = await axios.get(
              `https://graph.facebook.com/v19.0/${post.id}/insights?metric=impressions,reach,engagement,saved&access_token=${pageAccessToken}`
            );

            let views = 0;
            if (insightsResponse.data.data) {
              insightsResponse.data.data.forEach(insight => {
                if (insight.name === 'impressions' && insight.values[0]) {
                  views = insight.values[0].value;
                }
              });
            }

            return {
              id: post.id,
              title: post.caption?.substring(0, 60) + (post.caption?.length > 60 ? '...' : '') || `${post.media_type} Post`,
              description: post.caption || 'No caption available',
              date: new Date(post.timestamp).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              }),
              views: views,
              comments: post.comments_count || 0,
              likes: post.like_count || 0,
              shares: 0, // Instagram doesn't have shares in the same way
              watchTime: 0, // Would need video posts for this
              platform: 'instagram',
              image: post.media_url,
              url: post.permalink,
              created_time: post.timestamp,
              _raw: post
            };
          } catch (insightError) {
            console.error(`Error fetching insights for Instagram post ${post.id}:`, insightError);
            return {
              id: post.id,
              title: post.caption?.substring(0, 60) + (post.caption?.length > 60 ? '...' : '') || `${post.media_type} Post`,
              description: post.caption || 'No caption available',
              date: new Date(post.timestamp).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              }),
              views: 0,
              comments: post.comments_count || 0,
              likes: post.like_count || 0,
              shares: 0,
              watchTime: 0,
              platform: 'instagram',
              image: post.media_url,
              url: post.permalink,
              created_time: post.timestamp,
              _raw: post
            };
          }
        })
      );

      return postsWithInsights;
    } catch (error) {
      console.error('Error fetching Instagram posts:', error);
      throw new Error(`Failed to fetch Instagram posts: ${error.response?.data?.error?.message || error.message}`);
    }
  };

  // Generate recent posts from all connected accounts
  const generateRecentPosts = async (accounts) => {
    const allPosts = [];
    const errors = [];

    for (const account of accounts) {
      try {
        if (account.platform === 'facebook' && account.access_token) {
          for (const page of account.pages) {
            try {
              console.log(`Processing Facebook page: ${page.name} (${page.id})`);
              const posts = await fetchFacebookPosts(page.access_token, page.id);
              
              if (posts.length > 0) {
                const postsWithPageInfo = posts.map(post => ({
                  ...post,
                  pageName: page.name,
                  username: account.username
                }));
                allPosts.push(...postsWithPageInfo);
              }
            } catch (pageError) {
              errors.push(`Facebook page ${page.name}: ${pageError.message}`);
              console.error(`Error processing Facebook page ${page.name}:`, pageError);
            }
          }
        } else if (account.platform === 'instagram' && account.access_token && account.instagram_account_id) {
          try {
            console.log(`Processing Instagram account: ${account.username}`);
            const posts = await fetchInstagramPosts(account.access_token, account.instagram_account_id);
            
            if (posts.length > 0) {
              const postsWithAccountInfo = posts.map(post => ({
                ...post,
                pageName: account.username,
                username: account.username
              }));
              allPosts.push(...postsWithAccountInfo);
            }
          } catch (instagramError) {
            errors.push(`Instagram account ${account.username}: ${instagramError.message}`);
            console.error(`Error processing Instagram account ${account.username}:`, instagramError);
          }
        } else {
          console.log(`Skipping ${account.platform} - no token or unsupported platform`);
        }
      } catch (accountError) {
        errors.push(`${account.platform} account ${account.username}: ${accountError.message}`);
        console.error(`Error processing ${account.platform} account ${account.username}:`, accountError);
      }
    }

    // Log any errors
    if (errors.length > 0) {
      console.warn('Errors occurred while fetching posts:', errors);
      setError(`Some accounts failed to load: ${errors.join('; ')}`);
    }

    console.log('All real posts collected:', allPosts);
    
    // Sort by date and return
    const sortedPosts = allPosts
      .sort((a, b) => new Date(b.created_time) - new Date(a.created_time))
      .slice(0, 20); // Show more posts since we have real data
    
    return sortedPosts;
  };

  // Load recent posts on component mount
  useEffect(() => {
    const loadRecentPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const storedAccounts = JSON.parse(localStorage.getItem('socialAccounts') || '[]');
        console.log('Stored social accounts:', storedAccounts);
        setSocialAccounts(storedAccounts);
        
        if (storedAccounts.length > 0) {
          const posts = await generateRecentPosts(storedAccounts);
          setRecentPosts(posts);
        } else {
          console.log('No social accounts connected');
          setRecentPosts([]);
          setError('No social media accounts connected. Please connect accounts to view analytics.');
        }
      } catch (error) {
        console.error('Error loading recent posts:', error);
        setError(`Failed to load posts: ${error.message}`);
        setRecentPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadRecentPosts();
  }, []);

  // Refresh analytics data
  const refreshAnalytics = async () => {
    setRefreshing(true);
    setError(null);
    
    try {
      const storedAccounts = JSON.parse(localStorage.getItem('socialAccounts') || '[]');
      setSocialAccounts(storedAccounts);
      
      if (storedAccounts.length > 0) {
        const posts = await generateRecentPosts(storedAccounts);
        setRecentPosts(posts);
      } else {
        setRecentPosts([]);
        setError('No social media accounts connected.');
      }
    } catch (error) {
      console.error('Error refreshing analytics:', error);
      setError(`Failed to refresh: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Get platform icon
  const getPlatformIcon = (platform) => {
    return platformIcons[platform] || <FaFacebook className="text-blue-600" />;
  };

  return (
    <div className="py-4 bg-white flex min-h-screen text-gray-800">
      <SocialMediadashboardSidebar/>
      <div className="ml-8 flex-1">
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              ⚠️ {error}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-6 border-b border-gray-200 mb-6">
          {["Posts", "Reels", "Video", "Stories"].map((tab, idx) => (
            <button
              key={idx}
              className={`pb-3 text-sm font-semibold ${
                tab === "Posts"
                  ? "text-black border-b-2 border-black"
                  : "text-gray-400 hover:text-black"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <button 
              className="p-2 border rounded-md hover:bg-gray-100"
              onClick={refreshAnalytics}
              disabled={refreshing}
            >
              <FaSyncAlt className={refreshing ? 'animate-spin' : ''} />
            </button>
            <select className="border text-sm rounded-md p-1">
              <option>All Platforms</option>
              <option>Facebook</option>
              <option>Instagram</option>
              <option>Twitter</option>
              <option>LinkedIn</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center space-x-2">
            {["Today", "This Week", "This Month", "All Time"].map((filter) => (
              <button
                key={filter}
                className={`px-3 py-1 text-sm border rounded-md ${
                  filter === "All Time"
                    ? "bg-gray-800 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Connected Accounts Info */}
        {socialAccounts.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              📊 Showing real analytics from {socialAccounts.length} connected social media account{socialAccounts.length > 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Table Header */}
        <div className="grid grid-cols-7 text-sm font-semibold text-gray-600 border-b py-2">
          <div className="col-span-3">Post</div>
          <div className="text-center">Views</div>
          <div className="text-center">Comments</div>
          <div className="text-center">Likes</div>
          <div className="text-center">Shares</div>
          <div className="text-center">Watch Time</div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="col-span-7 text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading real post data from platforms...</p>
          </div>
        )}

        {/* No Posts State */}
        {!loading && recentPosts.length === 0 && (
          <div className="col-span-7 text-center py-8">
            <p className="text-gray-600">No posts found from connected accounts.</p>
            <p className="text-sm text-gray-500 mt-2">
              {socialAccounts.length === 0 
                ? "Connect social media accounts to see your posts."
                : "Make sure your accounts have posts and proper API permissions."
              }
            </p>
          </div>
        )}

        {/* Real Posts List */}
        {!loading && recentPosts.length > 0 && (
          <div className="space-y-2">
            {recentPosts.map((post) => (
              <div
                key={post.id}
                className="grid grid-cols-7 items-center border-b py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="col-span-3 flex items-center space-x-4">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-28 h-20 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-28 h-20 rounded-lg bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500 text-xs">No Image</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-lg">
                        {getPlatformIcon(post.platform)}
                      </span>
                      <h3 className="font-semibold text-sm">
                        {post.title}
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                      {post.description}
                    </p>
                    <div className="flex items-center space-x-3 text-xs text-gray-400">
                      <span>Published {post.date}</span>
                      <span>•</span>
                      <span className="font-medium">{post.pageName}</span>
                      <span>•</span>
                      <span className="capitalize">{post.platform}</span>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <span className="font-medium">{formatNumber(post.views)}</span>
                </div>
                <div className="text-center">
                  <span className="font-medium">{formatNumber(post.comments)}</span>
                </div>
                <div className="text-center">
                  <span className="font-medium">{formatNumber(post.likes)}</span>
                </div>
                <div className="text-center">
                  <span className="font-medium">{formatNumber(post.shares)}</span>
                </div>
                <div className="text-center">
                  <span className="font-medium">
                    {post.watchTime > 0 ? `${post.watchTime}m` : 'N/A'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Real Data Indicator */}
        {recentPosts.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-700 text-center">
              ✅ Showing real data from your social media platforms
            </p>
          </div>
        )}

        {/* Debug Info - Only show in development */}
        {process.env.NODE_ENV === 'development' && recentPosts.length > 0 && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <details>
              <summary className="cursor-pointer font-medium">
                Debug Info (Real Posts: {recentPosts.length})
              </summary>
              <pre className="text-xs mt-2 overflow-auto">
                {JSON.stringify(recentPosts.map(p => ({
                  id: p.id,
                  platform: p.platform,
                  title: p.title,
                  views: p.views,
                  likes: p.likes,
                  comments: p.comments,
                  shares: p.shares,
                  date: p.date
                })), null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentAnalytics;